import { useMemo } from "react"
import { LatLngBounds, LatLngTuple, PathOptions, Point } from 'leaflet'

import { SisetuResult } from "interface/sisetu"

import { SisetuMapLayerType, SisetuMapPoint } from "../types"
import { boundsIn, calcRadius } from "../util"
import { colors } from 'shared/colors'

const AREA_SCALE_PX = [30, 30]
const AREA_PPX_BY_ZOOM_LEVEL = [
  [0.25, 0.7],
  [0.2, 0.35],
  [0.14, 0.175],
  [0.08, 0.088],
  [0.04, 0.044],
  [0.02, 0.022],
  [0.01, 0.011],
  [0.005, 0.0055],
  [0.0027, 0.00275],
  [0.00135, 0.00137],
  [0.00067, 0.00068],
  [0.000338, 0.00034],
  [0.000169, 0.00017],
  [0.0000845, 0.000085],
  [0.0000422, 0.000043],
  [0.00002114, 0.0000215],
  [0.00001057, 0.0000107],
  [0.000005286, 0.00000535],
]

const STYLE_BY_SCORE: { [index: string]: PathOptions } = {
  dpc: { fillColor: colors.dpc.main },
  cancer: { fillColor: colors.cancer.main },
  trial: { fillColor: colors.trial.main },
  journal: { fillColor: colors.journal.main },
  senmoni: { fillColor: colors.senmoni.main },
}

type useSisetuMapOverlayProps = {
  sisetuList: SisetuResult[]
	zoomLevel: number
	mapBounds: LatLngBounds | null
	mapSize: Point | null
  selectedSisetuCode: string | null,
  showLayer: SisetuMapLayerType
}
export const useSisetuMapOverlay = ({
  sisetuList,
	zoomLevel,
  mapBounds,
	mapSize,
  selectedSisetuCode,
  showLayer
}: useSisetuMapOverlayProps) => useMemo(() => {
	if (!mapBounds || !mapSize) return null

	let dp = AREA_PPX_BY_ZOOM_LEVEL[zoomLevel]
	if (!dp) {
		dp = [
			(mapBounds.getNorth() - mapBounds.getSouth()) / mapSize.y,
			(mapBounds.getEast() - mapBounds.getWest()) / mapSize.x
		]
		//console.log(`zoom: ${mapInfo.zoomLevel}, dp: ${dp[0]}, ${dp[1]}`);
	}
	const points: SisetuMapPoint[] = []
	let current: SisetuResult | null = null
	for (const sisetu of sisetuList) {
		if (!sisetu.location || !sisetu.location.latitude || !sisetu.location.longitude) continue

		const score = (
			showLayer === 'pin' ? 0 :
			showLayer === 'dpc' ? sisetu.score?.dpc?.deviation :
			showLayer === 'cancer' ? sisetu.score?.cancer?.deviation :
			showLayer === 'trial' ? sisetu.score?.trial?.deviation :
			showLayer === 'journal' ? sisetu.score?.journal?.deviation :
			showLayer === 'senmoni' ? sisetu.score?.senmoni?.deviation : undefined
		)
		if (score === undefined || score === null) continue

		const radius = calcRadius(score)
		if (!boundsIn(
			mapBounds,
			sisetu.location.latitude,
			sisetu.location.longitude,
			radius
		)) continue

		const area = [
			Math.round(sisetu.location.longitude / (dp[0] * AREA_SCALE_PX[0])),
			Math.round(sisetu.location.latitude / (dp[1] * AREA_SCALE_PX[1])),
		] as LatLngTuple

		let point = points.find(p => p.position[0] === area[0] && p.position[1] === area[1])
		if (!point) {
			point = {
				isCurrent: false,
				position: area,
				sisetuList: [],
				score: 0
			}
			points.push(point)
		}
		point.sisetuList.push(sisetu)
		point.score = point.score + score
		if (sisetu.sisetuCode === selectedSisetuCode) {
			point.isCurrent = true
			current = sisetu
		}
	}
	for (const point of points) {
		let lat = 0
		let lng = 0
		let n = 0
		for (const sisetu of point.sisetuList) {
			if (!sisetu.location || !sisetu.location.latitude || !sisetu.location.longitude) continue
			lat += sisetu.location.latitude
			lng += sisetu.location.longitude
			n++
		}
		if (n > 0) point.position = [lat / n, lng / n]
	}
	return {
		points,
		pathOptions: STYLE_BY_SCORE[showLayer],
		current
	}
}, [
	sisetuList,
	zoomLevel,
	mapBounds,
	mapSize,
	selectedSisetuCode,
	showLayer
])